<script setup>
import { ref, computed } from 'vue'
import { Head, Link, useForm, router } from '@inertiajs/vue3'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import MuxVideoUploadViewer from '@/Components/MuxVideoUploadViewer.vue'
import PdfDocumentUpload from '@/Components/Forms/PdfDocumentUpload.vue'
import { Button } from '@/Components/ui/button'
import { Input } from '@/Components/ui/input'
import { Label } from '@/Components/ui/label'
import { Alert, AlertDescription } from '@/Components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select'
import { TagsInput, TagsInputInput, TagsInputItem, TagsInputItemDelete, TagsInputItemText } from '@/Components/ui/tags-input'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/Components/ui/alert-dialog'
import { AlertCircle } from 'lucide-vue-next'
import teacher from '@/routes/teacher'

const props = defineProps({
  content: Object,
  documentUrl: String,
  attachmentUrls: Array,
  subjects: Array,
  examboards: Array,
  levels: Array,
})

const isVideo = computed(() => props.content.type === 1 || props.content.type?.value === 1)
const isDocument = computed(() => props.content.type === 2 || props.content.type?.value === 2)

// Initialize existing attachments from props (for video content)
const existingAttachments = ref(
  (props.attachmentUrls || []).map((a) => ({
    path: a.path,
    original_name: a.original_name,
    size: a.size,
    url: a.url,
  }))
)

const form = useForm({
  title: props.content.title,
  subject_id: props.content.subject_id?.toString() || '',
  examboard_id: props.content.examboard_id?.toString() || '',
  level_id: props.content.level_id?.toString() || '',
  topics: props.content.topics ? props.content.topics.join(',') : '',
  mux_upload_id: '',
  document_path: '',
  document_original_name: props.content.attachments?.[0]?.original_name || '',
  document_size: props.content.attachments?.[0]?.size || 0,
  attachments: (props.attachmentUrls || []).map((a) => ({
    path: a.path,
    original_name: a.original_name,
    size: a.size,
  })),
})

const hasErrors = computed(() => Object.keys(form.errors).length > 0)

// Topics management
const topicsList = ref(props.content.topics || [])

const updateFormTopics = () => {
  form.topics = topicsList.value.join(',')
}

// Video upload state
const isVideoUploading = ref(false)
const videoError = ref(false)

const handleVideoUploadStart = () => {
  isVideoUploading.value = true
  videoError.value = false
}

const handleVideoUploadComplete = () => {
  isVideoUploading.value = false
  videoError.value = false
}

const handleVideoUploadError = () => {
  isVideoUploading.value = false
}

// Document upload state
const isDocUploading = ref(false)

const handleDocUploadStart = () => {
  isDocUploading.value = true
}

const handleDocUploadComplete = () => {
  isDocUploading.value = false
}

const handleDocUploadError = () => {
  isDocUploading.value = false
}

// Attachment upload state
const isAttachmentUploading = ref(false)
const attachmentPath = ref('')
const attachmentName = ref('')
const attachmentSize = ref(0)

const onAttachmentUploadComplete = (result) => {
  isAttachmentUploading.value = false
  form.attachments.push({
    path: result.path,
    original_name: result.original_name,
    size: result.size,
  })
  existingAttachments.value.push({
    path: result.path,
    original_name: result.original_name,
    size: result.size,
  })
  attachmentPath.value = ''
  attachmentName.value = ''
  attachmentSize.value = 0
}

const removeAttachment = (index) => {
  form.attachments.splice(index, 1)
  existingAttachments.value.splice(index, 1)
}

const isUploading = computed(() => isVideoUploading.value || isDocUploading.value || isAttachmentUploading.value)

const submitForm = () => {
  videoError.value = false
  form.put(teacher.content.update(props.content.id).url, {
    preserveScroll: true,
    onSuccess: () => {
      form.mux_upload_id = ''
      form.document_path = ''
    },
    onError: (errors) => {
      if (errors.mux_upload_id) {
        videoError.value = true
      }
    }
  })
}

const deleteContent = () => {
  router.delete(teacher.content.destroy(props.content.id).url)
}
</script>

<template>
  <Head :title="'Edit - ' + content.title" />

  <DashboardLayout>
    <div class="flex py-6 px-4 md:px-8 font-sans">
      <div class="w-full flex flex-col gap-6">
        <!-- Breadcrumb -->
        <nav class="flex items-center gap-2 text-sm text-gray-500">
          <Link :href="teacher.content.index().url" class="font-medium hover:underline hover:text-gray-700">
            My Content
          </Link>
          <span class="font-medium">/</span>
          <span class="text-gray-900 font-medium">Edit</span>
        </nav>

        <!-- Header -->
        <div class="flex flex-col gap-2 mb-4">
          <h1 class="text-gray-900 text-3xl md:text-4xl font-bold tracking-tight">Edit Learning Material</h1>
          <p class="text-gray-500 text-base">Update your {{ isDocument ? 'document' : 'video' }} content and details.</p>
        </div>

        <!-- Content Type Badge -->
        <div class="flex items-center gap-2">
          <span
            class="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
            :class="isDocument ? 'bg-orange-100 text-orange-700 border border-orange-200' : 'bg-blue-100 text-blue-700 border border-blue-200'"
          >
            <span class="material-symbols-outlined text-lg">{{ isDocument ? 'description' : 'videocam' }}</span>
            {{ isDocument ? 'Document' : 'Video' }}
          </span>
        </div>

        <!-- Form -->
        <form @submit.prevent="submitForm" class="flex flex-col gap-8">
          <!-- Form Fields Grid -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <!-- Title -->
            <div class="md:col-span-3 flex flex-col gap-2">
              <Label class="text-gray-900 text-sm font-semibold">Resource Title</Label>
              <Input
                v-model="form.title"
                required
                :class="form.errors.title ? '!border-red-300' : ''"
                placeholder="e.g., Introduction to Calculus - Derivatives"
              />
              <p v-if="form.errors.title" class="text-sm text-red-600">{{ form.errors.title }}</p>
            </div>

            <!-- Subject -->
            <div class="flex flex-col gap-2">
              <Label class="text-gray-900 text-sm font-semibold">Subject</Label>
              <Select v-model="form.subject_id" required>
                <SelectTrigger :class="form.errors.subject_id ? '!border-red-300' : ''">
                  <SelectValue placeholder="Select Subject..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="subject in subjects" :key="subject.id" :value="subject.id.toString()">
                    {{ subject.name }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p v-if="form.errors.subject_id" class="text-sm text-red-600">{{ form.errors.subject_id }}</p>
            </div>

            <!-- Exam Board -->
            <div class="flex flex-col gap-2">
              <Label class="text-gray-900 text-sm font-semibold">Exam Board</Label>
              <Select v-model="form.examboard_id" required>
                <SelectTrigger :class="form.errors.examboard_id ? '!border-red-300' : ''">
                  <SelectValue placeholder="Select Board..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="examboard in examboards" :key="examboard.id" :value="examboard.id.toString()">
                    {{ examboard.name }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p v-if="form.errors.examboard_id" class="text-sm text-red-600">{{ form.errors.examboard_id }}</p>
            </div>

            <!-- Level -->
            <div class="flex flex-col gap-2">
              <Label class="text-gray-900 text-sm font-semibold">Level</Label>
              <Select v-model="form.level_id" required>
                <SelectTrigger :class="form.errors.level_id ? '!border-red-300' : ''">
                  <SelectValue placeholder="Select Level..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="level in levels" :key="level.id" :value="level.id.toString()">
                    {{ level.name }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p v-if="form.errors.level_id" class="text-sm text-red-600">{{ form.errors.level_id }}</p>
            </div>

            <!-- Topics/Keywords -->
            <div class="md:col-span-3 flex flex-col gap-2">
              <Label class="text-gray-900 text-sm font-semibold">
                Topics/keywords <span class="text-gray-400 font-normal">(Max 5)</span>
              </Label>
              <TagsInput
                v-model="topicsList"
                @update:model-value="updateFormTopics"
                :max="5"
                class="h-[45px]"
              >
                <TagsInputItem v-for="item in topicsList" :key="item" :value="item">
                  <TagsInputItemText />
                  <TagsInputItemDelete />
                </TagsInputItem>
                <TagsInputInput
                  placeholder="Type and press Enter..."
                  :disabled="topicsList.length >= 5"
                />
              </TagsInput>
              <p class="text-xs text-gray-500">Tags help students find your content faster. Press Enter or comma to add.</p>
            </div>
          </div>

          <!-- Video Section -->
          <div v-if="isVideo" class="flex flex-col gap-6">
            <MuxVideoUploadViewer
              v-model="form.mux_upload_id"
              :playback-id="content.mux_playback_id"
              :title="content.title"
              :show-error="videoError"
              :allow-replacement="true"
              @upload:start="handleVideoUploadStart"
              @upload:complete="handleVideoUploadComplete"
              @upload:error="handleVideoUploadError"
            >
              <template #error>
                <p v-if="videoError" class="text-red-600 text-sm">Please upload a video before saving.</p>
              </template>
            </MuxVideoUploadViewer>

            <!-- Supporting Document for Video -->
            <div>
              <Label class="text-gray-900 text-sm font-semibold mb-3 block">
                Supporting Document <span class="text-gray-400 font-normal">(Optional)</span>
              </Label>
              <p class="text-xs text-gray-500 mb-3">Attach a PDF document as supporting material for this video.</p>

              <!-- Existing Attachments List -->
              <div v-if="existingAttachments.length > 0" class="flex flex-col gap-2 mb-3">
                <div
                  v-for="(attachment, index) in existingAttachments"
                  :key="index"
                  class="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
                >
                  <div class="flex items-center gap-3 min-w-0">
                    <span class="material-symbols-outlined text-red-500 text-xl shrink-0">picture_as_pdf</span>
                    <div class="min-w-0">
                      <p class="text-sm font-medium text-gray-900 truncate">{{ attachment.original_name }}</p>
                      <p v-if="attachment.size" class="text-xs text-gray-500">
                        {{ (attachment.size / (1024 * 1024)).toFixed(1) }} MB
                      </p>
                    </div>
                  </div>
                  <Button type="button" variant="ghost" size="sm" @click="removeAttachment(index)" class="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <span class="material-symbols-outlined text-sm">close</span>
                  </Button>
                </div>
              </div>

              <!-- Upload New Attachment -->
              <PdfDocumentUpload
                v-if="existingAttachments.length === 0"
                v-model="attachmentPath"
                v-model:original-name="attachmentName"
                v-model:file-size="attachmentSize"
                label="Upload Supporting Document"
                @upload:start="isAttachmentUploading = true"
                @upload:complete="onAttachmentUploadComplete"
                @upload:error="isAttachmentUploading = false"
              />
            </div>
          </div>

          <!-- Document Section -->
          <div v-if="isDocument">
            <Label class="text-gray-900 text-sm font-semibold mb-3 block">Document</Label>
            <PdfDocumentUpload
              v-model="form.document_path"
              v-model:original-name="form.document_original_name"
              v-model:file-size="form.document_size"
              :existing-url="documentUrl"
              @upload:start="handleDocUploadStart"
              @upload:complete="handleDocUploadComplete"
              @upload:error="handleDocUploadError"
            />
          </div>

          <!-- General Error Alert -->
          <Alert v-if="hasErrors" variant="destructive">
            <AlertCircle class="h-4 w-4" />
            <AlertDescription>
              Please correct the errors below before submitting.
            </AlertDescription>
          </Alert>

          <!-- Action Buttons -->
          <div class="flex items-center justify-between pt-4 border-t border-gray-200">
            <!-- Delete Button with AlertDialog -->
            <AlertDialog>
              <AlertDialogTrigger as-child>
                <Button
                  type="button"
                  variant="destructive"
                >
                  <span class="material-symbols-outlined mr-2">delete</span>
                  Delete Content
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete your content
                    and remove all associated data from our servers.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction @click="deleteContent">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <!-- Save/Cancel Buttons -->
            <div class="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                as-child
              >
                <Link :href="teacher.content.index().url">
                  Cancel
                </Link>
              </Button>
              <Button
                type="submit"
                :disabled="isUploading || form.processing"
              >
                <span v-if="form.processing" class="material-symbols-outlined animate-spin mr-2">sync</span>
                <span v-else class="material-symbols-outlined mr-2">check</span>
                {{ form.processing ? 'Saving...' : 'Save Changes' }}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </DashboardLayout>
</template>
